import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import './FeeSettings.css';

interface VehicleTypeFeeRule {
  _id?: string;
  ruleDescription: string;
  vehicleType: string;
  vehicleCategory: string;
  entryCalculationRule: string;
  exitCalculationRule: string;
  exitWeightCalculationRule: string;
  freeMinutes: number;
  maxAmount: number;
  isActive: boolean;
  priority: number;
  createdAt?: string;
  updatedAt?: string;
}

const FeeSettings: React.FC = () => {
  const [rules, setRules] = useState<VehicleTypeFeeRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<VehicleTypeFeeRule | null>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  const [formData, setFormData] = useState<VehicleTypeFeeRule>({
    ruleDescription: '',
    vehicleType: '',
    vehicleCategory: '',
    entryCalculationRule: '',
    exitCalculationRule: '',
    exitWeightCalculationRule: '',
    freeMinutes: 15,
    maxAmount: 100,
    isActive: true,
    priority: 1
  });

  // 選項數據
  const vehicleTypeOptions = [
    { value: 'small_car', label: '小轎' },
    { value: 'medium_car', label: '中型車' },
    { value: 'large_car', label: '大型車' },
    { value: 'motorcycle', label: '機車' },
    { value: 'truck', label: '貨車' },
    { value: 'bus', label: '巴士' }
  ];

  const vehicleCategoryOptions = [
    { value: 'temporary', label: '臨停車' },
    { value: 'monthly', label: '月租車' },
    { value: 'vip', label: 'VIP車' },
    { value: 'resident', label: '住戶車' },
    { value: 'visitor', label: '訪客車' },
    { value: 'disabled', label: '身障車' }
  ];

  const calculationRuleOptions = [
    { value: 'hourly_rate', label: '先算時段收費再用車種收費' },
    { value: 'vehicle_first', label: '先算車種收費再用時段收費' },
    { value: 'fixed_rate', label: '固定收費' },
    { value: 'progressive', label: '累進收費' },
    { value: 'time_based', label: '時段計費' }
  ];

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      // 模擬 API 調用
      const mockRules: VehicleTypeFeeRule[] = [
        {
          _id: '1',
          ruleDescription: '小轎車臨停收費規則',
          vehicleType: 'small_car',
          vehicleCategory: 'temporary',
          entryCalculationRule: 'hourly_rate',
          exitCalculationRule: 'hourly_rate',
          exitWeightCalculationRule: 'hourly_rate',
          freeMinutes: 15,
          maxAmount: 100,
          isActive: true,
          priority: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];
      
      setTimeout(() => {
        setRules(mockRules);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('載入收費規則失敗:', error);
      setSnackbar({
        open: true,
        message: '載入收費規則失敗',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule?: VehicleTypeFeeRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({ ...rule });
    } else {
      setEditingRule(null);
      setFormData({
        ruleDescription: '',
        vehicleType: '',
        vehicleCategory: '',
        entryCalculationRule: '',
        exitCalculationRule: '',
        exitWeightCalculationRule: '',
        freeMinutes: 15,
        maxAmount: 100,
        isActive: true,
        priority: 1
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editingRule) {
        // 更新規則
        const updatedRules = rules.map(rule => 
          rule._id === editingRule._id ? { ...formData, _id: editingRule._id } : rule
        );
        setRules(updatedRules);
        setSnackbar({
          open: true,
          message: '收費規則更新成功',
          severity: 'success'
        });
      } else {
        // 新增規則
        const newRule = {
          ...formData,
          _id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setRules(prev => [...prev, newRule]);
        setSnackbar({
          open: true,
          message: '收費規則新增成功',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('保存收費規則失敗:', error);
      setSnackbar({
        open: true,
        message: '保存收費規則失敗',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (ruleId: string) => {
    if (window.confirm('確定要刪除此收費規則嗎？')) {
      try {
        setRules(prev => prev.filter(rule => rule._id !== ruleId));
        setSnackbar({
          open: true,
          message: '收費規則刪除成功',
          severity: 'success'
        });
      } catch (error) {
        console.error('刪除收費規則失敗:', error);
        setSnackbar({
          open: true,
          message: '刪除收費規則失敗',
          severity: 'error'
        });
      }
    }
  };

  if (loading) {
    return (
      <Box className="fee-settings loading-container">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          載入中...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} className="fee-settings">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          收費設定
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新增車型收費規則
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>規則描述</TableCell>
              <TableCell>車型</TableCell>
              <TableCell>車種</TableCell>
              <TableCell>進場計算規則</TableCell>
              <TableCell>出場計算規則</TableCell>
              <TableCell>免費時間(分)</TableCell>
              <TableCell>最高收費(元)</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    暫無收費規則數據
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow key={rule._id}>
                  <TableCell>{rule.ruleDescription}</TableCell>
                  <TableCell>
                    {vehicleTypeOptions.find(opt => opt.value === rule.vehicleType)?.label || rule.vehicleType}
                  </TableCell>
                  <TableCell>
                    {vehicleCategoryOptions.find(opt => opt.value === rule.vehicleCategory)?.label || rule.vehicleCategory}
                  </TableCell>
                  <TableCell>
                    {calculationRuleOptions.find(opt => opt.value === rule.entryCalculationRule)?.label || rule.entryCalculationRule}
                  </TableCell>
                  <TableCell>
                    {calculationRuleOptions.find(opt => opt.value === rule.exitCalculationRule)?.label || rule.exitCalculationRule}
                  </TableCell>
                  <TableCell>{rule.freeMinutes}</TableCell>
                  <TableCell>{rule.maxAmount}</TableCell>
                  <TableCell>
                    <Chip
                      label={rule.isActive ? '啟用' : '停用'}
                      color={rule.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(rule)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(rule._id!)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增/編輯對話框 */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRule ? '編輯車型收費規則' : '新增車型收費規則'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="規則描述"
                required
                value={formData.ruleDescription}
                onChange={(e) => handleInputChange('ruleDescription', e.target.value)}
                placeholder="請輸入規則描述"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>車型</InputLabel>
                <Select
                  value={formData.vehicleType}
                  label="車型"
                  onChange={(e) => handleInputChange('vehicleType', e.target.value)}
                >
                  {vehicleTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>車種</InputLabel>
                <Select
                  value={formData.vehicleCategory}
                  label="車種"
                  onChange={(e) => handleInputChange('vehicleCategory', e.target.value)}
                >
                  {vehicleCategoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>進場計算規則</InputLabel>
                <Select
                  value={formData.entryCalculationRule}
                  label="進場計算規則"
                  onChange={(e) => handleInputChange('entryCalculationRule', e.target.value)}
                >
                  {calculationRuleOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>出場計算規則</InputLabel>
                <Select
                  value={formData.exitCalculationRule}
                  label="出場計算規則"
                  onChange={(e) => handleInputChange('exitCalculationRule', e.target.value)}
                >
                  {calculationRuleOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>出場重量計算規則</InputLabel>
                <Select
                  value={formData.exitWeightCalculationRule}
                  label="出場重量計算規則"
                  onChange={(e) => handleInputChange('exitWeightCalculationRule', e.target.value)}
                >
                  {calculationRuleOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="免費時間 (分)"
                type="number"
                required
                value={formData.freeMinutes}
                onChange={(e) => handleInputChange('freeMinutes', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 1440 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="最高收費 (元)"
                type="number"
                required
                value={formData.maxAmount}
                onChange={(e) => handleInputChange('maxAmount', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            取消
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<SaveIcon />}
          >
            {editingRule ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeSettings;